import uniqid from 'uniqid';
import isFn from '@utils/is-function';
import AjaxLoader from '../ajax-loader/ajax-loader';
import UpsCopyright from '../ups-copyright/ups-copyright';
import template, { radio as radioTemplate } from './templates/';
import { context as defaultContext } from './address-validation-defaults';
import { validationStatusCodes } from '@stores/address-book/address-book-store';

const $body = $(document.body);

const CLASS_NAME_INPUT_ERROR = '-error';

const CONFIRM_ORIGINAL_ADDRESS_VALUE = '_confirmedOriginalAddress_';

const DEFAULT_OPTIONS = {
    originFocusElement: null,
    preventFocusOrigin: false
};

const VALIDATION_STATUS_CODES = {
    VALID_ADDRESS: 0,
    SHOW_SUGGESTIONS: 1,
    INVALID_ADDRESS: 2, 
    UPS_UNAVAILABLE: 3
};

const logUpsServiceError = () => console.warn('UPS Service Unavailable: Address saved but not validated.');

const showSuggestions = (status) => status === VALIDATION_STATUS_CODES.SHOW_SUGGESTIONS || status === VALIDATION_STATUS_CODES.INVALID_ADDRESS;

export default class AddressValidation {
    #value = null;

    /**
     * @typedef {{ addressId: string|number, address: string, address2: string, city: string, stateCode: string, zip: string }} Address
     * 
     * @param {number} status status code of address entered by user
     * @param {Object<Address>} address object detailing user's currently entered address
     * @param {Array<Address>} suggestions array of address suggestions
     * @param {Object<string,any>} context object of dictionary mappings
     * @param {Ojbect<string,any>} options class configuration options
     * @param {Function?} onSubmitHandler function to call when user choice submitted
     */
    constructor(status, originalAddress, suggestions, context, options = {}, onSubmitHandler = null) {
        context = { ...defaultContext, ...context };
        options = { ...DEFAULT_OPTIONS, ...options };

        const $parsed = $($.parseHTML(template));
        this.$el = $parsed;

        this.$cancelLabel = this.$el.find('[data-label="cancel"]');
        this.$confirmCheckboxLabel = this.$el.find('[data-label="confirmCheckbox"]');
        this.$confirmRadioLabel = this.$el.find('[data-label="confirmRadio"]');
        this.$enteredAddressLabel = this.$el.find('[data-label="enteredAddress"]');
        this.$headingLabel = this.$el.find('[data-label="heading"]');
        this.$saveLabel = this.$el.find('[data-label="save"]');
        this.$suggestedAddressLabel = this.$el.find('[data-label="suggestedAddress"]');

        this.$checkbox = this.$el.find('[data-region="checkbox"]');
        this.$checkboxError = this.$el.find('[data-region="checkboxError"]');
        this.$description = this.$el.find('[data-region="description"]');
        this.$enteredAddress = this.$el.find('[data-region="enteredAddress"]');
        this.$error = this.$el.find('[data-region="error"]');
        this.$invalid = this.$el.find('[data-region="invalid"]');
        this.$radio = this.$el.find('[data-region="radio"]');
        this.$radioError = this.$el.find('[data-region="radioError"]');
        this.$suggestedAddress = this.$el.find('[data-region="suggestedAddress"]');

        this.$closeCtrl = this.$el.find('[data-control="close"]');
        this.$confirmCheckboxCtrl = this.$el.find('[data-control="confirmCheckbox"]');
        this.$confirmRadioCtrl = this.$el.find('[data-control="confirmRadio"]');
        this.$formCtrl = this.$el.find('[data-control="form"]');
        this.$radioCtrl = this.$el.find('[data-control="radio"]');

        this.$loaderPlaceholder = this.$el.find('[data-placeholder="loader"]');
        this.$upsPlaceholder = this.$el.find('[data-placeholder="ups"]');

        this.loader = null;

        this.id = uniqid.time('_addressValidation_');

        this.status = status;
        this.originalAddress = originalAddress || {};
        this.suggestions = suggestions || [];
        this.context = context;
        this.onSubmitHandler = isFn(onSubmitHandler) || null;

        this.originFocusElement = options.originFocusElement || document.activeElement;
        this.preventFocusOrigin = options.preventFocusOrigin;

        this.isValidated = false;

        this.init();
    }

    get #isOriginal() {
        return this.#value === CONFIRM_ORIGINAL_ADDRESS_VALUE;
    }

    get isInvalidAddress() {
        return this.status === validationStatusCodes.INVALID_ADDRESS;
    }

    destroy() {
        if(this.$el.is(':visible')) {
            this.$el.on('hidden.bs.modal', () => this.$el.remove());
            this.hide();
        } else {
            this.$el.remove();
        }
    }

    init() {
        this.renderLabels();
        this.renderRegions();
        this.setupIds();
        this.setupLoader();
        this.setupUps();
        this.toggleView();
        this.setupListeners();
        this.render();
    }

    onHidden() {
        if(!this.preventFocusOrigin) {
            this.originFocusElement?.focus();
        }
    }

    onInputChange({ checked, type, value }) {
        if(type === 'checkbox') {
            this.#value = checked ? value : null;
        } else {
            this.#value = value;
        }
    }

    onInvalidInput($input, $errorEl, errorId) {
        if($input.filter(':checked').length) {
            $input
                .removeClass(CLASS_NAME_INPUT_ERROR)
                .removeAttr('aria-describedby aria-invalid');
            
            $errorEl.hide();
        } else {
            $input
                .addClass(CLASS_NAME_INPUT_ERROR)
                .attr({ 'aria-describedby': errorId, 'aria-invalid': true });
            
            $errorEl.show();
        }
    }

    async onSubmit(evt) {
        evt.preventDefault();
        evt.stopPropagation();

        if(this.$formCtrl.get(0).checkValidity()) {
            if(this.onSubmitHandler) {
                this.setLoading();
                this.$error.hide().html();

                const { success, error } = await this.onSubmitHandler(this.#isOriginal, this.#value);

                if(success) {
                    this.isValidated = true;
                    this.hide();
                } else if(error) {
                    this.$error.show().html(error);
                }

                this.setLoading(false);
            } else {
                this.hide();
            }
        } else {
            this.validate();
        }
    }

    parseAddress({ address, address2, city, stateCode, zip }) {
        let street = address;

        if(address2) {
            street += ` ${address2}`;
        }

        return `${street}, ${city}, ${stateCode} ${zip}`;
    }

    render() {
        $body.append(this.$el);
    }

    renderLabels() {
        this.$headingLabel.html(this.context.AddressValidation);

        this.$cancelLabel.html(this.context.Cancel);
        this.$confirmCheckboxLabel.html(this.context.ConfirmAddress);
        this.$confirmRadioLabel.html(this.context.ConfirmAddress);
        this.$enteredAddressLabel.html(this.context.AddressEntered);
        this.$headingLabel.html(this.context.AddressValidation);
        this.$saveLabel.html(this.context.SaveAddress);
        this.$suggestedAddressLabel.html(this.context.UseSuggestedAddress);
    }

    renderRegions() {
        this.$checkboxError.html(this.context.UnknownAddressFieldError);
        this.$description.html(this.context.SelectSuggestedAddress);
        this.$enteredAddress.html(this.parseAddress(this.originalAddress));
        this.$invalid.html(this.context.UnknownAddress);
        this.$radioError.html(this.context.SelectSuggestedFieldError);

        this.$confirmCheckboxCtrl.attr('value', CONFIRM_ORIGINAL_ADDRESS_VALUE);
        this.$confirmRadioCtrl.attr('value', CONFIRM_ORIGINAL_ADDRESS_VALUE);
    }

    renderSuggestions() {
        const fragment = document.createDocumentFragment();

        this.suggestions.forEach((address) => {
            const label = this.parseAddress(address)
            const radioHtmlStr = radioTemplate({
                id: `${this.id}_suggestions_radio_${address.addressId}_`,
                label,
                value: address.addressId
            });
            
            const [ radioNode ] = $.parseHTML(radioHtmlStr.trim());
            fragment.appendChild(radioNode);
        });

        this.$radio.html(fragment);

        this.$radioCtrl = this.$el.find('[data-control="radio"]');
    }

    setupConfirmationView() {
        this.$description.remove();
        this.$suggestedAddress.remove();
    }

    setupIds() {
        const confirmCheckboxId = `${this.id}_confirm_checkbox_`;
        const confirmRadioId = `${this.id}_confirm_radio_`;
        const headingId = `${this.id}_heading_`;
        const suggestedHeadingId = `${this.id}_suggested_heading_`;

        this.$headingLabel.attr('id', headingId);
        this.$el.attr({
            'id': this.id,
            'aria-labeledby': headingId
        });

        this.$suggestedAddressLabel.attr('id', suggestedHeadingId);
        this.$suggestedAddress.attr('aria-labelledby', suggestedHeadingId);

        this.$confirmCheckboxCtrl.attr('id', confirmCheckboxId);
        this.$confirmCheckboxLabel.attr('for', confirmCheckboxId);
        this.$confirmRadioCtrl.attr('id', confirmRadioId);
        this.$confirmRadioLabel.attr('for', confirmRadioId);
    }

    setupListeners() {
        this.$el.on('hidden.bs.modal', () => this.onHidden());
        this.$formCtrl.on('submit', (evt) => this.onSubmit(evt));

        this.$confirmCheckboxCtrl.on('change', (evt) => this.onInputChange(evt.target));
        const $radios = this.$radioCtrl.add(this.$confirmRadioCtrl.get(0));
        $radios.on('change', (evt) => this.onInputChange(evt.target));
    }

    setupLoader() {
        this.loader = new AjaxLoader(this.$loaderPlaceholder, {
            light: true,
            replaceOriginEl: false,
            size: 'xs',
            srText: this.context.LoadingSrText
        });

        this.loader.render();
    }

    setLoading(loading = true) {
        if(this.loader) this.loader.active = loading;
    }

    setupSuggestionsView() {
        this.$checkbox.remove();
        this.$invalid.remove();

        this.renderSuggestions();
    }

    setupUps() {
        const context = { 
            logo: {
                alt: this.context.UpsLogo.Alt,
                url: this.context.UpsLogo.Url
            },
            text: this.context.UpsCopyright
        };

        const ups = new UpsCopyright(
            this.$upsPlaceholder,
            { replaceOriginEl: true },
            context
        );

        ups.render();
    }

    toggleView() {
        if(this.isInvalidAddress) {
            this.setupConfirmationView();
        } else {
            this.setupSuggestionsView();
        }
    }

    show() {
        $(`#${this.id}`).modal('show');
    }

    hide() {
        $(`#${this.id}`).modal('hide');
    }

    prompt() {
        this.show();

        return new Promise((resolve) => {
            this.$el.one('hide.bs.modal', () => {
                resolve({
                    isValidated: this.isValidated,
                    isOriginal: this.#isOriginal,
                    value: this.#value
                });
            });
        });
    }

    validate() {
        const errorId = `${this.id}_error_`;
        if(this.isInvalidAddress) {
            this.$confirmCheckboxCtrl
                .addClass(CLASS_NAME_INPUT_ERROR)
                .attr({ 'aria-describedby': errorId, 'aria-invalid': true })
                .on('change', () => this.onInvalidInput(this.$confirmCheckboxCtrl, this.$checkboxError, errorId));
                
            this.$checkboxError
                .show()
                .attr('id', errorId);
        } else {
            const $radioCtrls = this.$radioCtrl.add(this.$confirmRadioCtrl.get(0));
            $radioCtrls
                .addClass(CLASS_NAME_INPUT_ERROR)
                .attr({ 'aria-describedby': errorId, 'aria-invalid': true })
                .on('change', () => this.onInvalidInput($radioCtrls, this.$radioError, errorId));
            
            this.$radioError
                .show()
                .attr('id', errorId);
        }

        this.$formCtrl.find('[aria-invalid=true]').get(0)?.focus();
    }
}

export {
    VALIDATION_STATUS_CODES as statusCodes,
    logUpsServiceError,
    showSuggestions
};
